import { Checkbox, CheckboxSize, Show } from "shared/components";
import { PopoverSearchInput } from "../components/PopoverSearchInput";
import { Avatar } from "shared/components/Avatar";
import {
  Popover,
  PopoverTrigger,
  PopoverContent
} from "shared/components/Popover";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDown01Icon,
  UserCircle02Icon,
  UserMinus01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { cx } from "shared/utils";
import { DropdownLabels, NO_ASSIGNEE_ID } from "../constants";
import { Card } from "shared/components/Card";
import { useQuery } from "@tanstack/react-query";
import { useAuthContext } from "providers/Auth";
import { useMemo, useState } from "react";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";

import { api } from "api/task";

type AssigneeFilterPopoverProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUserIds: number[];
  onSelectedUserIdsChange: (userIds: number[]) => void;
  onPageChange: (page: string) => void;
};

export const AssigneeFilterPopover = ({
  open,
  onOpenChange,
  selectedUserIds,
  onSelectedUserIdsChange,
  onPageChange
}: AssigneeFilterPopoverProps) => {
  const { auth } = useAuthContext();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 500);
  const { data: assignees, isLoading } = useQuery({
    queryKey: ["TASK_ASSIGNEES", debouncedSearch],
    queryFn: () => api.getAssignees(debouncedSearch || undefined),
    enabled: open
  });

  const handleToggleUser = (userId: number) => {
    const isSelected = selectedUserIds.includes(userId);
    if (isSelected)
      onSelectedUserIdsChange(selectedUserIds.filter((id) => id !== userId));
    else onSelectedUserIdsChange([...selectedUserIds, userId]);

    onPageChange("1");
  };

  const handleSelectAll = () => {
    onSelectedUserIdsChange([]);
    onPageChange("1");
    onOpenChange(false);
  };

  const isAllSelected = selectedUserIds.length === 0;
  const selectedCount = selectedUserIds.length;

  const sortedUsers = useMemo(() => {
    const users = assignees || [];
    const sorted = users.toSorted((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });
    if (auth) {
      const authInResults = sorted.some((u) => u.id === auth.id);
      if (authInResults) {
        const withoutAuth = sorted.filter((u) => u.id !== auth.id);
        return [auth, ...withoutAuth];
      }
    }
    return sorted;
  }, [assignees, auth]);

  const selectedUsersData = useMemo(() => {
    if (selectedCount === 0) return [];
    return selectedUserIds
      .map((id) => {
        if (id === NO_ASSIGNEE_ID)
          return {
            id,
            name: "No assignee",
            isCurrentUser: false,
            isNoAssignee: true
          };
        const isCurrentUser = id === auth?.id;
        const user = sortedUsers.find((u) => u.id === id);
        const name = user?.name || (isCurrentUser ? auth.name : null);
        if (!name) return null;
        return { id, name, isCurrentUser, isNoAssignee: false };
      })
      .filter(Boolean) as {
      id: number;
      name: string;
      isCurrentUser: boolean;
      isNoAssignee: boolean;
    }[];
  }, [selectedUserIds, sortedUsers, auth, selectedCount]);

  return (
    <Popover open={open} onOpenChange={onOpenChange} placement="bottom-start">
      <PopoverTrigger onClick={() => onOpenChange(true)}>
        <Card className="w-full px-4 py-2 flex flex-wrap items-start gap-2 min-h-10 shadow-xs">
          <HugeiconsIcon
            icon={UserCircle02Icon}
            className="text-content-200 mt-0.5"
            size={20}
          />
          <div
            className={cx(
              "flex items-center gap-2 flex-wrap flex-1 min-w-0",
              isAllSelected ? "mt-1" : "mt-0.5"
            )}
          >
            <span className="text-content-200 font-bold text-xs">
              {DropdownLabels.assignee}:{" "}
            </span>

            <Show if={isAllSelected}>
              <span className="font-bold text-content-1000 text-xs">Any</span>
            </Show>
            <Show if={selectedUsersData.length > 0}>
              {selectedUsersData
                .slice(
                  0,
                  selectedUsersData.length > 2 ? 1 : selectedUsersData.length
                )
                .map((user, index, arr) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-center gap-1 shrink-0"
                  >
                    {user.isNoAssignee ? (
                      <HugeiconsIcon
                        icon={UserMinus01Icon}
                        className="text-content-1000"
                        size={20}
                      />
                    ) : (
                      <Avatar
                        name={user.name}
                        className="text-[9px] text-content-1000"
                        size={20}
                      />
                    )}
                    <span className="font-bold text-content-1000 text-xs whitespace-nowrap">
                      {user.name}
                      {user.isCurrentUser && (
                        <span className="text-content-200"> (You)</span>
                      )}
                      {index < arr.length - 1 && (
                        <span className="text-content-200">,</span>
                      )}
                    </span>
                  </div>
                ))}
              {selectedUsersData.length > 2 && (
                <span className="font-bold text-content-200 text-xs whitespace-nowrap">
                  +{selectedUsersData.length - 1}
                </span>
              )}
            </Show>
          </div>
          <HugeiconsIcon
            icon={ArrowDown01Icon}
            size={16}
            className={cx("text-content-1000 mt-1", open ? "rotate-180" : "")}
          />
        </Card>
      </PopoverTrigger>
      <PopoverContent className="max-w-[314px] w-full p-0 rounded-lg border border-alpha-100 shadow-lg bg-content-1000-inverted z-10">
        <div className="flex w-full items-center justify-start gap-3 px-4 py-2.5 border-b border-alpha-40">
          <Checkbox
            onChange={handleSelectAll}
            checked={isAllSelected}
            size={CheckboxSize.SM}
            wrapperClassName="!w-auto !flex-none !p-0 justify-center"
          />
          <span className="text-base font-medium text-content-1000">Any</span>
        </div>
        <PopoverSearchInput
          id="assignee-filter-search"
          placeholder="Search assignee"
          value={search}
          onChange={setSearch}
        />
        <div className="max-h-[300px] overflow-y-auto">
          <Show if={!search}>
            <div
              className="flex w-full items-center justify-start gap-3 px-4 py-2.5 hover:bg-alpha-40 cursor-pointer"
              onClick={() => handleToggleUser(NO_ASSIGNEE_ID)}
            >
              <Checkbox
                checked={selectedUserIds.includes(NO_ASSIGNEE_ID)}
                size={CheckboxSize.SM}
                wrapperClassName="!w-auto !flex-none !p-0 justify-center"
                onChange={() => {}}
              />
              <HugeiconsIcon
                icon={UserMinus01Icon}
                className="text-content-1000"
                size={20}
              />
              <p className="text-base font-medium text-content-1000">
                No assignee
              </p>
            </div>
          </Show>
          <Show if={isLoading}>
            <div className="px-4 py-2.5 text-content-400 text-sm">
              Loading users...
            </div>
          </Show>
          <Show if={!isLoading && sortedUsers.length === 0}>
            <div className="px-4 py-2.5 text-content-400 text-sm">
              No users found
            </div>
          </Show>
          {sortedUsers.map((user) => {
            const isSelected = selectedUserIds.includes(user.id);
            const isCurrentUser = user.id === auth!.id;
            return (
              <div
                key={user.id}
                className="flex w-full items-center justify-start gap-3 px-4 py-2.5 hover:bg-alpha-40 cursor-pointer"
                onClick={() => handleToggleUser(user.id)}
              >
                <Checkbox
                  checked={isSelected}
                  size={CheckboxSize.SM}
                  wrapperClassName="!w-auto !flex-none !p-0 justify-center"
                  onChange={() => {}}
                />
                <Avatar
                  name={user.name}
                  className="text-[9px] text-content-1000"
                  size={22}
                />
                <p className="text-base font-medium text-content-1000">
                  {user.name}
                  {isCurrentUser && (
                    <span className="text-content-200">&nbsp;(You)</span>
                  )}
                </p>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
